import { useEffect, useRef, useState } from "react";
import { Link } from "wouter";
import { BarChart3, ChevronDown, KeyRound, Loader2, LogOut, Settings, ShieldCheck, Tags } from "lucide-react";
import { api, type Me } from "../lib/api";

const MIN = 10;

function Logo() {
  return (
    <div className="mb-6 flex items-center gap-2.5">
      <svg width="32" height="32" viewBox="0 0 32 32" aria-hidden>
        <rect width="32" height="32" rx="7" fill="#C2410C" />
        <path d="M7 23l6-7 4 4 3-3 5 6z" fill="white" /><circle cx="21" cy="11" r="2.6" fill="white" />
      </svg>
      <div className="leading-tight">
        <div className="text-[16px] font-semibold tracking-tight">Bildbanken</div>
        <div className="text-[11.5px] text-muted">Sölvesborg-Mjällby Sparbank</div>
      </div>
    </div>
  );
}

function Card({ children, onSubmit }: { children: React.ReactNode; onSubmit: (e: React.FormEvent) => void }) {
  return (
    <div className="grid min-h-screen place-items-center px-4">
      <form className="card w-full max-w-sm p-7 shadow-sm" onSubmit={onSubmit}>
        <Logo />
        {children}
      </form>
    </div>
  );
}

function Field(props: { label: string; type?: string; value: string; onChange: (v: string) => void; autoFocus?: boolean; autoComplete?: string; hint?: string }) {
  return (
    <div className="mb-3">
      <label className="label">{props.label}</label>
      <input className="field" type={props.type ?? "text"} value={props.value} autoFocus={props.autoFocus}
        autoComplete={props.autoComplete} onChange={(e) => props.onChange(e.target.value)} />
      {props.hint && <p className="mt-1 text-[12px] text-muted">{props.hint}</p>}
    </div>
  );
}

export function Login({ onDone }: { onDone: () => void }) {
  const [email, setEmail] = useState("");
  const [pw, setPw] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  return (
    <Card onSubmit={async (e) => {
      e.preventDefault(); setBusy(true); setErr("");
      try { await api("auth/login", { method: "POST", json: { email, password: pw } }); onDone(); }
      catch (x) { setErr(x instanceof Error ? x.message : "Inloggningen misslyckades"); }
      finally { setBusy(false); }
    }}>
      <Field label="E-post" type="email" value={email} onChange={setEmail} autoFocus autoComplete="username" />
      <Field label="Lösenord" type="password" value={pw} onChange={setPw} autoComplete="current-password" />
      {err && <p className="mb-3 text-bad">{err}</p>}
      <button className="btn-primary w-full justify-center py-2" disabled={busy}>
        {busy && <Loader2 size={15} className="animate-spin" />} Logga in
      </button>
      <p className="mt-4 text-[12px] text-muted">Glömt lösenordet? Be en administratör återställa det.</p>
    </Card>
  );
}

/** Första start: inga konton finns. Visas bara när sidan öppnas på samma dator som servern. */
export function Setup({ onDone }: { onDone: () => void }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [err, setErr] = useState("");
  return (
    <Card onSubmit={async (e) => {
      e.preventDefault(); setErr("");
      if (pw !== pw2) return setErr("Lösenorden är inte lika.");
      try { await api("auth/setup", { method: "POST", json: { name, email, password: pw } }); onDone(); }
      catch (x) { setErr(x instanceof Error ? x.message : "Fel"); }
    }}>
      <div className="mb-4 flex gap-2 rounded-lg bg-ember-soft/60 p-3 text-[13px] text-soft">
        <ShieldCheck size={16} className="mt-0.5 shrink-0 text-ember" />
        <span>Välkommen! Skapa det första administratörskontot. Därefter bjuder du in övriga under Inställningar.</span>
      </div>
      <Field label="Namn" value={name} onChange={setName} autoFocus autoComplete="name" />
      <Field label="E-post" type="email" value={email} onChange={setEmail} autoComplete="username" />
      <Field label="Lösenord" type="password" value={pw} onChange={setPw} autoComplete="new-password" hint={`Minst ${MIN} tecken.`} />
      <Field label="Upprepa lösenord" type="password" value={pw2} onChange={setPw2} autoComplete="new-password" />
      {err && <p className="mb-3 text-bad">{err}</p>}
      <button className="btn-primary w-full justify-center py-2">Skapa admin och logga in</button>
    </Card>
  );
}

export function ChangePassword({ forced, onDone }: { forced?: boolean; onDone: () => void }) {
  const [cur, setCur] = useState("");
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [err, setErr] = useState("");
  const [ok, setOk] = useState(false);
  const form = (
    <>
      {forced && <p className="mb-4 text-soft">Du har loggat in med ett tillfälligt lösenord. Välj ett eget för att fortsätta.</p>}
      <Field label={forced ? "Tillfälligt lösenord" : "Nuvarande lösenord"} type="password" value={cur} onChange={setCur} autoFocus autoComplete="current-password" />
      <Field label="Nytt lösenord" type="password" value={pw} onChange={setPw} autoComplete="new-password" hint={`Minst ${MIN} tecken. Du loggas ut på andra enheter.`} />
      <Field label="Upprepa nytt lösenord" type="password" value={pw2} onChange={setPw2} autoComplete="new-password" />
      {err && <p className="mb-3 text-bad">{err}</p>}
      {ok && <p className="mb-3 text-ok">Lösenordet är bytt.</p>}
      <button className="btn-primary w-full justify-center py-2">Spara nytt lösenord</button>
    </>
  );
  const submit = async (e: React.FormEvent) => {
    e.preventDefault(); setErr("");
    if (pw !== pw2) return setErr("Lösenorden är inte lika.");
    try { await api("auth/password", { method: "POST", json: { current: cur, password: pw } }); setOk(true); setTimeout(onDone, forced ? 0 : 900); }
    catch (x) { setErr(x instanceof Error ? x.message : "Fel"); }
  };
  if (forced) return <Card onSubmit={submit}>{form}</Card>;
  return (
    <div className="mx-auto max-w-sm pt-10">
      <h1 className="mb-4 text-xl font-semibold">Byt lösenord</h1>
      <form className="card p-6" onSubmit={submit}>{form}</form>
    </div>
  );
}

export function UserMenu({ user }: { user: Me }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const close = (e: MouseEvent) => { if (!ref.current?.contains(e.target as Node)) setOpen(false); };
    const esc = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", close);
    document.addEventListener("keydown", esc);
    return () => { document.removeEventListener("mousedown", close); document.removeEventListener("keydown", esc); };
  }, []);
  const initials = user.name.split(/\s+/).map((p) => p[0]).slice(0, 2).join("").toUpperCase();
  return (
    <div ref={ref} className="relative">
      <button onClick={() => setOpen(!open)} className="flex items-center gap-1.5 rounded-full py-0.5 pl-0.5 pr-1.5 hover:bg-mist" aria-label="Kontomeny">
        <span className="grid h-8 w-8 place-items-center rounded-full bg-ink text-[12px] font-semibold text-white">{initials}</span>
        <ChevronDown size={14} className="text-muted" />
      </button>
      {open && (
        <div className="absolute right-0 top-full z-50 mt-1 w-60 overflow-hidden rounded-xl border border-line bg-card shadow-lg">
          <div className="border-b border-line px-4 py-3">
            <div className="truncate font-medium">{user.name}</div>
            <div className="truncate text-[12px] text-muted">{user.email}</div>
            <div className="mt-1 text-[11.5px] font-medium text-ember">{user.role === "admin" ? "Administratör" : "Användare"}</div>
          </div>
          {user.role === "admin" && <>
            <Link href="/installningar" onClick={() => setOpen(false)} className="flex items-center gap-2 px-4 py-2 hover:bg-mist"><Settings size={14} /> Inställningar</Link>
            <Link href="/statistik" onClick={() => setOpen(false)} className="flex items-center gap-2 px-4 py-2 hover:bg-mist"><BarChart3 size={14} /> Statistik</Link>
            <Link href="/taggar" onClick={() => setOpen(false)} className="flex items-center gap-2 px-4 py-2 hover:bg-mist"><Tags size={14} /> Hantera taggar</Link>
          </>}
          <Link href="/konto" onClick={() => setOpen(false)} className="flex items-center gap-2 px-4 py-2 hover:bg-mist"><KeyRound size={14} /> Byt lösenord</Link>
          <button className="flex w-full items-center gap-2 px-4 py-2 text-left hover:bg-mist" onClick={async () => {
            await api("auth/logout", { method: "POST" }).catch(() => {});
            location.href = "/";
          }}><LogOut size={14} /> Logga ut</button>
        </div>
      )}
    </div>
  );
}
